import { useQuery } from "@tanstack/react-query";

import {
  IStaffDetails,
  IStaffListItem,
  IStaffUser,
} from "../../../@types/admin/AdminStaff";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { staffService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export const STAFF_LIST_QUERY_KEY = ["admin-staff-list"];

export function useStaffList() {
  const { data, isLoading, isError, isFetching } = useQuery<
    IPaginatedResponse<IStaffListItem[]>
  >({
    queryKey: STAFF_LIST_QUERY_KEY,
    queryFn: async () => ensureOk(await staffService.getStaffList()),
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return {
    staff: data?.result ?? [],
    isLoading,
    isError,
    isFetching,
  };
}

export function useStaffDetails(staffId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IStaffDetails>({
    queryKey: ["admin-staff-details", staffId],
    queryFn: async () => ensureOk(await staffService.getStaffById(staffId!)),
    enabled: Boolean(staffId),
    refetchOnMount: "always",
  });

  return { staffDetails: data, isLoading, isError };
}

/**
 * The staff member's linked user account. `userId` comes from the staff
 * response, so this stays idle until that has loaded — the same dependency
 * Retool expressed with `queryDisabled` on `getStaffUserDetails`.
 */
export function useStaffUser(userId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IStaffUser>({
    queryKey: ["admin-staff-user", userId],
    queryFn: async () => ensureOk(await staffService.getUserById(userId!)),
    enabled: Boolean(userId),
    refetchOnMount: "always",
  });

  return { staffUser: data, isLoading, isError };
}
